import { Button, Col, Menu, Row, BackTop } from "antd";
import { UpCircleFilled } from '@ant-design/icons';
import "antd/dist/antd.css";
import {
  useBalance,
  useContractLoader,
  useContractReader,
  useGasPrice,
  useOnBlock,
  useUserProviderAndSigner,
} from "eth-hooks";
import { useExchangeEthPrice } from "eth-hooks/dapps/dex";
import React, { useCallback, useEffect, useState } from "react";
import { Link, Route, Switch, useLocation } from "react-router-dom";
import { enquireScreen } from 'enquire-js';
import "./App.css";
import {
  Contract,
  ThemeSwitch,
  Header2,
  Footer,
  CollectionBanner,
  CollectionAbout,
  CollectionGallery,
  NavDao
} from "./components";

// Import the data content for the navigation, team and features block details
import {
  NavDataSource,
  TeamDataSource,
  FeaturesDataSource,
} from './components/data.source';


import { NETWORKS, ALCHEMY_KEY } from "./constants";
import externalContracts from "./contracts/external_contracts";
// contracts
import deployedContracts from "./contracts/hardhat_contracts.json";
import { Transactor, Web3ModalSetup } from "./helpers";
import { Home, ExampleUI, Hints, Subgraph } from "./views";
import { useStaticJsonRPC } from "./hooks";

const { ethers } = require("ethers");
/*
    Welcome to 🏗 scaffold-eth !

    Code:
    https://github.com/scaffold-eth/scaffold-eth

    Support:
    https://t.me/joinchat/KByvmRe5wkR-8F_zz6AjpA
    or DM @austingriffith on twitter or telegram

    You should get your own Alchemy.com & Infura.io ID and put it in `constants.js`
    (this is your connection to the main Ethereum network for ENS etc.)


    🌏 EXTERNAL CONTRACTS:
    You can also bring in contract artifacts in `constants.js`
    (and then use the `useExternalContractLoader()` hook!)
*/

/// 📡 What chain are your contracts deployed to?
const initialNetwork = NETWORKS.fantom; // <------- select your target frontend network (localhost, rinkeby, xdai, mainnet)
//const initialNetwork = NETWORKS.localhost; // <------- select your target frontend network (localhost, rinkeby, xdai, mainnet)

// 😬 Sorry for all the console logging
const DEBUG = false;
const NETWORKCHECK = true;
const USE_BURNER_WALLET = false; // toggle burner wallet feature
const USE_NETWORK_SELECTOR = false;

const web3Modal = Web3ModalSetup();

// 🛰 providers
const providers = [
  "https://eth-mainnet.gateway.pokt.network/v1/lb/611156b4a585a20035148406",
  `https://eth-mainnet.alchemyapi.io/v2/${ALCHEMY_KEY}`,
  "https://rpc.scaffoldeth.io:48544",
];

function App(props) {
  // specify all the chains your app is available on. Eg: ['localhost', 'mainnet', ...otherNetworks ]
  // reference './constants.js' for other networks
  const networkOptions = [initialNetwork.name, "fantom", "testnetFantom"];

  const [injectedProvider, setInjectedProvider] = useState();
  const [address, setAddress] = useState();
  //const [selectedNetwork, setSelectedNetwork] = useState(networkOptions[2]);
  const [selectedNetwork, setSelectedNetwork] = useState(networkOptions[1]);
  const location = useLocation();

  const targetNetwork = NETWORKS[selectedNetwork];

  // 🔭 block explorer URL
  const blockExplorer = targetNetwork.blockExplorer;

  // load all your providers
  const localProvider = useStaticJsonRPC([
    process.env.REACT_APP_PROVIDER ? process.env.REACT_APP_PROVIDER : targetNetwork.rpcUrl,
  ]);
  const mainnetProvider = useStaticJsonRPC(providers);

  if (DEBUG) console.log(`Using ${selectedNetwork} network`);

  // 🛰 providers
  if (DEBUG) console.log("📡 Connecting to Mainnet Fantom");

  const logoutOfWeb3Modal = async () => {
    await web3Modal.clearCachedProvider();
    if (injectedProvider && injectedProvider.provider && typeof injectedProvider.provider.disconnect == "function") {
      await injectedProvider.provider.disconnect();
    }
    setTimeout(() => {
      window.location.reload();
    }, 1);
  };

  /* 💵 This hook will get the price of ETH from 🦄 Uniswap: */
  const price = useExchangeEthPrice(targetNetwork, mainnetProvider);

  /* 🔥 This hook will get the price of Gas from ⛽️ EtherGasStation */
  const gasPrice = useGasPrice(targetNetwork, "fast");
  // Use your injected provider from 🦊 Metamask or if you don't have it then instantly generate a 🔥 burner wallet.
  const userProviderAndSigner = useUserProviderAndSigner(injectedProvider, localProvider, USE_BURNER_WALLET);
  const userSigner = userProviderAndSigner.signer;
  const userProvider = userProviderAndSigner.provider;

  if (DEBUG) console.log("userProviderAndSigner", userProviderAndSigner);

  const getAddress = async () => {
    if (userSigner) {
      const newAddress = await userSigner.getAddress();
      setAddress(newAddress);
    }
  };

  useEffect(() => {
    async function getAddress() {
      if (userSigner) {
        const newAddress = await userSigner.getAddress();
        setAddress(newAddress);
      }
    }
    getAddress();
  }, [userSigner]);

  // You can warn the user if you would like them to be on a specific network
  const localChainId = localProvider && localProvider._network && localProvider._network.chainId;
  const selectedChainId =
    userSigner && userSigner.provider && userSigner.provider._network && userSigner.provider._network.chainId;

  // For more hooks, check out 🔗eth-hooks at: https://www.npmjs.com/package/eth-hooks

  // 🏗 scaffold-eth is full of handy hooks like this one to get your balance:
  const yourLocalBalance = useBalance(localProvider, address, 10000);

  // const contractConfig = useContractConfig();

  const contractConfig = { deployedContracts: deployedContracts || {}, externalContracts: externalContracts || {} };

  // Load in your local 📝 contract and read a value from it:
  const readContracts = useContractLoader(localProvider, contractConfig);

  // If you want to make 🔐 write transactions to your contracts, use the userSigner:
  const writeContracts = useContractLoader(userSigner, contractConfig, localChainId);

  // EXTERNAL CONTRACT EXAMPLE:
  //
  // If you want to bring in the mainnet DAI contract it would look like:
  const mainnetContracts = useContractLoader(mainnetProvider, contractConfig);

  // If you want to call a function on a new block
  useOnBlock(mainnetProvider, () => {
    console.log(`⛓ A new mainnet block is here: ${mainnetProvider._lastBlockNumber}`);
  });

  // keep track of a variable from the contract in the local React state:
  const balance = useContractReader(readContracts, "MoonDaoNFT", "balanceOf", [address], 10000);

  // const totalSupply = 0;
  // const mintingFee = 0;
  const totalSupply = useContractReader(readContracts, "MoonDaoNFT", "totalSupply", 10000);
  const mintingFee = useContractReader(readContracts, "MoonDaoNFT", "getPrice", 10000);
  const whiteListEnabled = useContractReader(readContracts, "MoonDaoNFT", "whitelistStatus", [address], 10000);
  const mintOpened = useContractReader(readContracts, "MoonDaoNFT", "getOpenMintStatus", 10000);  
  const freeMintOpened = useContractReader(readContracts, "MoonDaoNFT", "getOpenFreeMintStatus", 10000);  
  
  //
  // 🧫 DEBUG 👨🏻‍🔬
  //
  useEffect(() => {
    if (
      DEBUG &&
      mainnetProvider &&
      address &&
      selectedChainId &&
      yourLocalBalance &&
      readContracts &&
      writeContracts &&
      mainnetContracts
    ) {
      console.log("_____________________________________ 🏗 scaffold-eth _____________________________________");
      console.log("🌎 mainnetProvider", mainnetProvider);
      console.log("🏠 localChainId", localChainId);
      console.log("👩‍💼 selected address:", address);
      console.log("🕵🏻‍♂️ selectedChainId:", selectedChainId);
      console.log("💵 yourLocalBalance", yourLocalBalance ? ethers.utils.formatEther(yourLocalBalance) : "...");
      console.log("📝 readContracts", readContracts);
      console.log("🌍 DAI contract on mainnet:", mainnetContracts);
      console.log("🔐 writeContracts", writeContracts);
      console.log("🌍 totalSupply:", totalSupply);
      console.log("🌍 mintingFee:", mintingFee);
      console.log("🌍 whiteListEnabled:", whiteListEnabled);
      console.log("🌍 mintOpened:", mintOpened);
      console.log("🌍 freeMintOpened:", freeMintOpened);
      console.log("🌍 balanceOfNFT:", balance);
    }
  }, [
    mainnetProvider,
    address,
    selectedChainId,
    yourLocalBalance,
    readContracts,
    writeContracts,
    mainnetContracts,
    localChainId,
  ]);

  const loadWeb3Modal = useCallback(async () => {
    const provider = await web3Modal.connect();
    setInjectedProvider(new ethers.providers.Web3Provider(provider));
    getAddress();

    provider.on("chainChanged", chainId => {
      console.log(`chain changed to ${chainId}! updating providers`);
      setInjectedProvider(new ethers.providers.Web3Provider(provider));
      getAddress();
    });

    provider.on("accountsChanged", () => {
      console.log(`account changed!`);
      setInjectedProvider(new ethers.providers.Web3Provider(provider));
      getAddress();
    });

    // Subscribe to session disconnection
    provider.on("disconnect", (code, reason) => {
      console.log(code, reason);
      logoutOfWeb3Modal();
    });
    // eslint-disable-next-line
  }, [setInjectedProvider]);

  useEffect(() => {
    if (web3Modal.cachedProvider) {
      loadWeb3Modal();
    }
  }, [loadWeb3Modal]);

  const [isFirstScreen, setIsFirstScreen] = useState(true);
  const onEnterChange = (mode) => {
    setIsFirstScreen(mode === 'enter');
  };

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    enquireScreen((b) => {
      setIsMobile(b);
    });
  }, [setIsMobile]);

  return (
    <div className="App">
      {/* ✏️ Edit the header and change the title to your project name */}
      {/* <Header2
        id="Nav"
        key="Nav"
        dataSource={NavDataSource}
        isMobile={isMobile}
        address={address}
        web3Modal={web3Modal}
        loadWeb3Modal={loadWeb3Modal}
        logoutOfWeb3Modal={logoutOfWeb3Modal}  
      /> */}
      <NavDao/>
      <Switch>
        <Route exact path="/mint">
          <CollectionBanner key="banner" 
              onEnterChange={onEnterChange} 
              signer={userSigner}
              provider={userProvider}
              address={address}
              blockExplorer={blockExplorer}
              totalSupply={totalSupply}
              price={mintingFee}
              balance={yourLocalBalance}
              web3Modal={web3Modal}
              loadWeb3Modal={loadWeb3Modal}
              logoutOfWeb3Modal={logoutOfWeb3Modal}
              whiteListEnabled={whiteListEnabled}     
              contract={writeContracts.MoonDaoNFT}
              freeMintOpened={freeMintOpened}
              publicSaleEnabled={mintOpened}
          /> 
          {/* <CollectionAbout /> */}
          <CollectionGallery
            contract={readContracts.MoonDaoNFT}
            nftbalance={balance}
            provider={localProvider}
            address={address}
            web3Modal={web3Modal}
          />
        </Route>
        <Route exact path="/mint/debug">
          {/*
                🎛 this scaffolding is full of commonly used components
                this <Contract/> component will automatically parse your ABI
                and give you a form to interact with it locally
            */}

          <Contract
            name="MoonDaoNFT"
            price={price}
            signer={userSigner}
            provider={localProvider}
            address={address}
            blockExplorer={blockExplorer}
            contractConfig={contractConfig}
          />
        </Route>
      </Switch>
      {/* <Footer /> */}
      <BackTop>
        <UpCircleFilled style={{ fontSize: '28px', color: '#f1c4a7' }}/>
      </BackTop>     
      {/* <ThemeSwitch /> */}
    </div>
  );
}

export default App;
